import { forwardRef, memo, useEffect, useRef } from 'react';

import { HeaderAction } from '@/components/Header/Action/HeaderAction';
import { useCoreUIDataContext } from '@/widgets/CoreUIDataProvider/context';
import { useCoreUIDataEntry } from '@/widgets/CoreUIDataProvider/CoreUIDataProvider';

import type { CartResponse } from './api-types';
import type { HeaderActionProps } from '@/components/Header/Action/HeaderAction';

/**
 * cart 도메인 응답의 안전 변환 (unknown → CartResponse | null).
 *
 * invalid shape (host 가 잘못 매핑했거나 서버 응답이 예상과 다른 경우) 은 `null` 반환.
 * widget 은 null 을 받으면 `showDot=false` 처리.
 *
 * @see plan/cart-and-fetcher-override-design.md — §5-4
 */
export function mapCartResponse(raw: unknown): CartResponse | null {
  if (raw === null || typeof raw !== 'object') return null;

  const hasItem = (raw as { hasItem?: unknown }).hasItem;
  if (typeof hasItem !== 'boolean') return null;

  return { hasItem };
}
/**
 * `Header.CartAction` — cart 도메인 widget.
 *
 * Provider 의 cart entry 를 자동 구독하여 `showDot` 을 결정. host 는 icon / href / label /
 * meta / visibleOn 등 *render 결정* prop 만 작성.
 *
 * # 동작 매트릭스
 *
 * | 상황 | entry.status | showDot |
 * |---|---|---|
 * | Provider 없음 | (context null) | `false` + dev warn (1회) |
 * | fetcher 미주입 | `error` (no endpoint) | `false`. Provider onError 호출 |
 * | fetcher 있음 + fetch 실패 | `error` | `false`. Provider onError 호출 |
 * | 첫 mount | `idle` / `loading` | `false` (직전 success 없음) |
 * | add-to-cart 후 refetch | `loading` | 직전 success 의 hasItem 유지 (stale-while-revalidating) |
 * | 정상 응답 | `success` | `mapCartResponse(data)?.hasItem ?? false` |
 *
 * **핵심 규칙**:
 * - `error` → 캐시 강제 `false` 리셋 (`"없는데 보이는"` 케이스 방지)
 * - `success` → 캐시 갱신
 * - `idle` / `loading` → 캐시 유지
 *
 * # host 가 fetcher 미주입 + cart dot 직접 통제 원하면
 *
 * `Header.CartAction` 대신 `<Header.Action action="cart" showDot={hostHasItem} />` 직접 사용.
 * dumb primitive 라 Provider 의존 0.
 *
 * @see plan/cart-and-fetcher-override-design.md — §5-4
 */
export interface HeaderCartActionProps extends Omit<HeaderActionProps, 'showDot' | 'action'> {
  /** 식별자 기본값 `'cart'`. tracking 차원에서 override 가능 (e.g., `'cart-mini'`) */
  action?: string;
}

export const HeaderCartAction = memo(
  forwardRef<HTMLButtonElement, HeaderCartActionProps>(({ action = 'cart', ...rest }, ref) => {
    const ctx = useCoreUIDataContext();
    // Provider 부재 시 enabled=false 로 hook 내부 warn 미발생. 별도 widget warn 으로 안내.
    const { entry } = useCoreUIDataEntry('cart', { enabled: ctx !== null });

    // 상태별 캐시 정책 (Q5 매트릭스):
    //   success → 새 hasItem 으로 갱신
    //   error   → false 로 리셋 ("없는데 보이는" 방지)
    //   idle/loading → 캐시 유지 (stale-while-revalidating)
    //
    // entry.data 는 mapped type 으로 CartResponse 가 강제되지만 — host fetcher 가 type 위반 응답을
    // 보낼 가능성 (BFF 가 schema 변경 등) 차단을 위해 여전히 mapCartResponse 가드 사용.
    const lastHasItemRef = useRef(false);
    if (entry.status === 'success') {
      lastHasItemRef.current = mapCartResponse(entry.data)?.hasItem ?? false;
    } else if (entry.status === 'error') {
      lastHasItemRef.current = false;
    }
    const showDot = lastHasItemRef.current;

    // Provider 부재 warn 은 effect 안에서 1회만 호출 — StrictMode / dev re-render spam 방지.
    const warnedRef = useRef(false);
    useEffect(() => {
      if (process.env.NODE_ENV === 'production') return;
      if (ctx !== null) return;
      if (warnedRef.current) return;
      warnedRef.current = true;
      // eslint-disable-next-line no-console
      console.warn(
        '[core-ui] Header.CartAction without <CoreUIDataProvider>. ' +
          'showDot stays false. Wrap in <CoreUIDataProvider> with fetchers.cart, ' +
          'or use <Header.Action action="cart"> directly with your own showDot.',
      );
    }, [ctx]);

    return <HeaderAction {...rest} action={action} ref={ref} showDot={showDot} />;
  }),
);

HeaderCartAction.displayName = 'Header.CartAction';
